import type { paths } from "../types/api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

type APIBPA =
  paths["/api/v1/bpas/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["bpa"];

// UI types - map termId → id and parentId to string for component compatibility
export type BPA = Omit<APIBPA, "termId" | "parentId"> & {
  id: string;
  parentId?: string | null;
  slug: string;
  children?: BPATreeNode[];
};
export type BPATreeNode = BPA & { children: BPATreeNode[] };

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

export type BPAListResponse =
  paths["/api/v1/bpas"]["get"]["responses"]["200"]["content"]["application/json"];
export type BPASingleResponse =
  paths["/api/v1/bpas/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
export type BPAChildrenResponse =
  paths["/api/v1/bpas/{id}/children"]["get"]["responses"]["200"]["content"]["application/json"];
export type BPAAncestorsResponse =
  paths["/api/v1/bpas/{id}/ancestors"]["get"]["responses"]["200"]["content"]["application/json"];

// ============================================================================
// Helper functions
// ============================================================================

function mapBPA(b: BPAListResponse["bpas"][0]): BPA {
  return {
    ...b,
    id: b.termId,
    parentId: b.parentId ?? null,
    slug: b.slug ?? "",
  };
}

// The OpenAPI generated type only shows children one level deep, but runtime data is recursive
type BPAWithRecursiveChildren = BPAChildrenResponse["bpas"][0] & {
  children?: BPAWithRecursiveChildren[];
};

function mapBPATree(b: BPAWithRecursiveChildren): BPATreeNode {
  return {
    ...b,
    id: b.termId,
    parentId: b.parentId ?? null,
    slug: b.slug ?? "",
    children: b.children?.map(mapBPATree) || [],
  };
}

// ============================================================================
// Service
// ============================================================================

export const bpas = {
  getAll: async (
    parentId?: string,
    search?: string,
    fields?: "minimal" | "full",
  ): Promise<BPA[]> => {
    const params = new URLSearchParams();
    if (parentId !== undefined) params.append("parentId", parentId);
    if (search) params.append("search", search);
    if (fields) params.append("fields", fields);

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<BPAListResponse>(`/v1/taxonomy/bpas${query}`);
    return response.bpas.map(mapBPA);
  },

  getById: async (id: string): Promise<BPA> => {
    const response = await api.get<BPASingleResponse>(`/v1/taxonomy/bpas/${id}`);
    return mapBPA(response.bpa);
  },

  getChildren: async (id: string, maxDepth?: number): Promise<BPATreeNode[]> => {
    const params = maxDepth ? `?maxDepth=${maxDepth}` : "";
    const response = await api.get<BPAChildrenResponse>(
      `/v1/taxonomy/bpas/${id}/children${params}`,
    );
    return (response.bpas as BPAWithRecursiveChildren[]).map(mapBPATree);
  },

  getAncestors: async (id: string): Promise<BPA[]> => {
    const response = await api.get<BPAAncestorsResponse>(`/v1/taxonomy/bpas/${id}/ancestors`);
    return response.ancestors.map(mapBPA);
  },

  create: async (data: BPASingleResponse["bpa"]): Promise<BPA> => {
    const response = await api.post<BPASingleResponse>("/v1/taxonomy/bpas", data);
    return mapBPA(response.bpa);
  },

  update: async (id: string, data: BPASingleResponse["bpa"]): Promise<BPA> => {
    const response = await api.put<BPASingleResponse>(`/v1/taxonomy/bpas/${id}`, data);
    return mapBPA(response.bpa);
  },

  move: async (id: string, parentId: string | null): Promise<BPA> => {
    const response = await api.put<BPASingleResponse>(`/v1/taxonomy/bpas/${id}/move`, {
      parentId,
    });
    return mapBPA(response.bpa);
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/taxonomy/bpas/${id}`);
  },
};

// Alias for backward compatibility
export const businessPracticeAreas = bpas;
